import { getCowbellTranslations } from '../../../../i18n/translations';

const { t } = getCowbellTranslations();

export enum SubjectivityTypes {
  FILL_IN = 'FILL_IN',
  ATTEST = 'ATTEST',
  UPLOAD = 'UPLOAD',
  YES_NO = 'YES_NO',
}

export const bindSteps = {
  POLICY_HOLDER_INFORMATION: 'Policy Holder Contact',
  UPDATE_COMMISSION: 'Commission Percentage',
  AGENCY_INFORMATION: 'Surplus Lines Tax Documentation',
  SECURITY_QUESTIONS: 'Missing Security Questions',
  POLICY_EFFECTIVE_DATE: 'Policy Effective Date',
  DILLIGENT_SEARCH_REPORT: 'Diligent Search Report',
  MANUFACTURING: 'Manufacturing',
  TECH_E_O_COV_CONFIRMATION: 'Tech E&O Coverage Confirmation',
  NO_KNOWN_LOSS_LETTER: 'No Known Loss Letter',
  NO_KNOWN_LOSS_LETTER_AU: 'No Claims Declaration',
  LOSS_RUNS: 'Loss Runs',
  INSURANCE_APPLICATION: 'Insurance Application',
  RENEWAL_APPLICATION: 'Renewal Application',
  BIND_QUOTE: 'Bind Quote',
  P100_BIND_QUOTE: 'Request to Bind',
  ADDITIONAL_NAMED_INSURED: 'Additional Named Insured Affidavit',
  ISSUE_POLICY: 'Issue Policy',
} as const;

export const subjectivityKeys = {
  POLICY_HOLDER_CONTACT: 'S001',
  SURPLUS_LINES_TAX_DOCUMENTATION: 'S002',
  DILLIGENT_SEARCH_REPORT: 'S003',
  MANUFACTURING: 'S004',
  TECH_E_O_COV_CONFIRMATION: 'S007',
  NO_KNOWN_LOSS_LETTER: 'S006',
  LOSS_RUNS: 'S005',
  INSURANCE_APPLICATION: 'S008',
  ADDITIONAL_NAMED_INSURED: 'S009',
  RENEWAL_APPLICATION: 'S025',
  P100_INSURANCE_APPLICATION: 'S026',
  P100_RENEWAL_APPLICATION: 'S027',
  PRE_ISSUE_NKLL: 'S028',
  PRE_ISSUE_LOSS_RUNS: 'S029',
  SECURITY_QUESTIONS: 'S036',
  PONE_NO_KNOWN_LOSS_LETTER: 'S037',
  P100PRO_INSURANCE_APPLICATION: 'S038',
  PONE_INSURANCE_APPLICATION: 'S039',
  P100PRO_RENEWAL_APPLICATION: 'S040',
  PONE_RENEWAL_APPLICATION: 'S041',
  P_TECH_INSURANCE_APP: 'S045',
  P_TECH_RENEWAL_APP: 'S046',
  P_TECH_NKLL: 'S047',
  P_TECH_LOSS_RUNS: 'S049',
  P_TECH_LARGEST_CONTRACT: 'S065',
  ZSP_INSURANCE_APP: 'S100',
  ZSP_WARRANTY_STATEMENT: 'S156',
  ZSP_REDUCTION_IN_FORCE: 'S157',
  POLICY_HOLDER_CONTACT_PRE_ISSUANCE: 'S102',
  PONETECH_INSURANCE_APPLICATION: 'S086',
  POLICY_HOLDER_CONTACT_AU: 'S103',
  MANUFACTURING_AU: 'S104',
  LOSS_RUNS_AU: 'S105',
  NO_KNOWN_LOSS_LETTER_AU: 'S106',
  TECH_E_O_COV_CONFIRMATION_AU: 'S107',
  INSURANCE_APPLICATION_AU: 'S108',
  ADDITIONAL_NAMED_INSURED_AU: 'S109',
  BCP_IRP: 'S125',
  EDR: 'S126',
  STATEMENT_OF_FACTS_AU: 'S129',
  // Prime One US
  PONE_US_MANUFACTURING: 'S131',
  PONE_US_RESILIENCE_MEETING: 'S133',
  PONE_US_CONTINGENT_PLANNING: 'S134',
  PONE_US_ADDITIONAL_NAMED_INSURED: 'S135',
  PONE_US_RTO: 'S136',
  PONE_US_RENEWAL_APPLICATION: 'S137',
  PONE_US_PCI_COMPLIANCE: 'S138',
  PONE_US_EOL_SOFTWARE_RETIREMENT: 'S139',
  PONE_US_SEPARATE_BREACH_FUND: 'S140',
  PONE_US_RECORD_COUNT: 'S141',
  PONE_US_EXTORTION_THREAT_MFA: 'S142',
  PONE_US_EXTORTION_THREAT_PATCHING: 'S143',
  PONE_US_EXTORTION_THREAT_BACKUPS: 'S144',
  PONE_US_PARENT_COMPANY_SEGREGATION: 'S145',
  PONE_US_DARK_WEB_CONTENT: 'S146',
  PONE_US_PROOF_OF_PRIOR_COVERAGE: 'S147',
  PONE_US_INSURANCE_APPLICATION: 'S148',
  PONE_US_CRS_MEETING_HIGHER_RISK: 'S149',
  PONE_US_CYBER_AWARENESS_TRAINING: 'S150',
  PONE_US_FL_SURPLUS_DISCLOSURE: 'S151',
  // Prime One Plus US (Excess) - new excess-specific subjectivities
  EXCESS_UNDERLYING_SUBJECTIVITIES: 'S152',
  EXCESS_SECURITY_CONTACT: 'S153',
  EXCESS_HOLDING_COMPANY: 'S154',
  EXCESS_UNDERLYING_DOCUMENTS: 'S155',
} as const;

export const GENERIC_SUBJECTIVITIES = {
  S048: {
    type: SubjectivityTypes.UPLOAD,
  },
  S152: {
    type: SubjectivityTypes.ATTEST,
    prompt:
      "Please confirm that the underlying carrier's subjectivities and conditions have been satisfied/cleared.",
  },
  S154: {
    type: SubjectivityTypes.ATTEST,
    prompt:
      'Please confirm the holding company / named entity seeking coverage.',
  },
  S155: {
    type: SubjectivityTypes.UPLOAD,
    prompt:
      'Please provide all binders or policy documents for underlying layers.',
  },
  S050: {
    type: SubjectivityTypes.ATTEST,
    prompt: `Please confirm the Applicant's Business Continuity / Disaster Recovery plan's Recovery Time Objective (RTO) for the amount of time within which business processes and continuity must be restored is equal to or less than 12 hours. *Note - If greater than 12 hours all waiting periods will be increased to 12 hours.`,
  },
  S051: {
    type: SubjectivityTypes.ATTEST,
    prompt: `Please confirm the application represents all entities seeking coverage under this policy, their nature of operations and relationship to the first named insured by ownership percentage, and also includes necessary exposures.`,
  },
  S052: {
    type: SubjectivityTypes.ATTEST,
    prompt: `Please confirm the applicant is not suggesting to clients the use of any pixels or tracking cookies on their website. Please note, we may have follow up questions if this is the case.`,
  },
  S053: {
    type: SubjectivityTypes.ATTEST,
    prompt: `Please confirm the applicant does not collect, capture, purchase, receive through trade, or otherwise obtain biometric identifiers or biometric information (including, but not limited to, iris scans, fingerprints, fingerprint, voiceprint, or scan of hand or face geometry). *Please note, we will have follow up questions if biometric data is collected. A BIPA exclusion may need to be applied depending on favorable responses.`,
  },
  S054: {
    type: SubjectivityTypes.ATTEST,
    prompt: `Out of band authentication is a secondary verification method with the requestor of a funds transfer through a communication channel separate from the original request. Please confirm this is required by the applicant.`,
  },
  S055: {
    type: SubjectivityTypes.ATTEST,
  },
  S056: {
    type: SubjectivityTypes.ATTEST,
  },
  S057: {
    type: SubjectivityTypes.ATTEST,
  },
  S058: {
    type: SubjectivityTypes.ATTEST,
  },
  S059: {
    type: SubjectivityTypes.UPLOAD,
    prompt: `Please upload an approximate breakdown by percentage of how revenue is generated from products or services.`,
  },
  S060: {
    type: SubjectivityTypes.ATTEST,
    prompt: `Please confirm the applicant is not involved in any payment processing or POS technology, banking payment technology, live financial trading software, social networks or dating sites, peer to peer sites, data aggregation, health information processing or storage, gaming or gambling software, control systems automation, telecommunications services including ISP, medical software, hardware manufacture or wearable technology development. Terms assume no.`,
  },
  S061: {
    type: SubjectivityTypes.ATTEST,
    prompt: `Please confirm the applicant ensures subcontractors carry their own insurance. Please provide what coverage and what limit of liability they ensure they carry.`,
  },
  S062: {
    type: SubjectivityTypes.ATTEST,
  },
  S063: {
    type: SubjectivityTypes.FILL_IN,
  },
  S064: {
    type: SubjectivityTypes.ATTEST,
  },
  S066: {
    type: SubjectivityTypes.FILL_IN,
    prompt:
      'Please confirm that insured does not hold, process or outsource more than 250,000 records. If more than 250,000, please describe the type of sensitive information that is held, and for each, provide an appropriate number of unique records that is processed and / or stored and expect to process and / or store in the next 12 and 24 months',
  },
  S067: {
    type: SubjectivityTypes.FILL_IN,
  },
  S068: {
    type: SubjectivityTypes.ATTEST,
  },
  S069: {
    type: SubjectivityTypes.FILL_IN,
    prompt: 'Please describe here:',
  },
  S070: {
    type: SubjectivityTypes.FILL_IN,
    prompt:
      'Please describe procedures for: preventing infringement on third party intellectual property rights; obtaining licenses to use third party intellectual property rights; and the monitoring of use of third party intellectual property rights.',
  },
  S071: {
    type: SubjectivityTypes.FILL_IN,
    prompt: `Please describe the procedures employed to respond to complaints for inappropriate or infringing content (such as take down procedures) or inappropriate online or offline activities of users of the platform (such as banning members or law enforcement notification).`,
  },
  S072: {
    type: SubjectivityTypes.FILL_IN,
    prompt: `Please describe here:`,
  },
  S073: {
    type: SubjectivityTypes.ATTEST,
  },
  S075: {
    type: SubjectivityTypes.FILL_IN,
    prompt: `Yes/No`,
  },
  S076: {
    type: SubjectivityTypes.FILL_IN,
    prompt: `Yes/No`,
  },
  S077: {
    type: SubjectivityTypes.FILL_IN,
  },
  S078: {
    type: SubjectivityTypes.FILL_IN,
  },
  S079: {
    type: SubjectivityTypes.FILL_IN,
  },
  S080: {
    type: SubjectivityTypes.FILL_IN,
  },
  S081: {
    type: SubjectivityTypes.FILL_IN,
  },
  S082: {
    type: SubjectivityTypes.FILL_IN,
  },
  S083: {
    type: SubjectivityTypes.FILL_IN,
  },
  S084: {
    type: SubjectivityTypes.FILL_IN,
  },
  S085: {
    type: SubjectivityTypes.FILL_IN,
  },
  S087: {
    type: SubjectivityTypes.FILL_IN,
  },
  S088: {
    type: SubjectivityTypes.FILL_IN,
  },
  S089: {
    type: SubjectivityTypes.FILL_IN,
  },
  S090: {
    type: SubjectivityTypes.ATTEST,
  },
  S091: {
    type: SubjectivityTypes.ATTEST,
  },
  S092: {
    type: SubjectivityTypes.ATTEST,
  },
  S093: {
    type: SubjectivityTypes.ATTEST,
  },
  S094: {
    type: SubjectivityTypes.ATTEST,
  },
  S095: {
    type: SubjectivityTypes.FILL_IN,
  },
  S096: {
    type: SubjectivityTypes.FILL_IN,
  },
  S097: {
    type: SubjectivityTypes.FILL_IN,
  },
  S098: {
    type: SubjectivityTypes.FILL_IN,
  },
  S099: {
    type: SubjectivityTypes.FILL_IN,
  },
  S124: {
    type: SubjectivityTypes.UPLOAD,
  },
  S127: {
    type: SubjectivityTypes.UPLOAD,
  },
  S128: {
    type: SubjectivityTypes.UPLOAD,
    prompt:
      'Please upload the signed Cowbell Supplemental Attestation for broadened Separate Breach Fund Costs coverage.',
  },
  S130: {
    type: SubjectivityTypes.UPLOAD,
    prompt:
      'Download the MFA attestation template below, complete and sign it, then upload the signed document.',
  },
  S131: {
    type: SubjectivityTypes.FILL_IN,
    prompt:
      'Please provide additional details about the OT/IT environment segregation (if applicable).',
  },
  S135: {
    type: SubjectivityTypes.UPLOAD,
  },
  S136: {
    type: SubjectivityTypes.YES_NO,
  },
  S137: {
    type: SubjectivityTypes.UPLOAD,
  },
  S138: {
    type: SubjectivityTypes.ATTEST,
    prompt: `Please confirm the insured is PCI compliant, or that PCI is being handled by a payment processor from the point of transaction.`,
  },
  S139: {
    type: SubjectivityTypes.ATTEST,
  },
  S140: {
    type: SubjectivityTypes.YES_NO,
  },
  S141: {
    type: SubjectivityTypes.FILL_IN,
  },
  S142: {
    type: SubjectivityTypes.UPLOAD,
  },
  S143: {
    type: SubjectivityTypes.UPLOAD,
  },
  S144: {
    type: SubjectivityTypes.UPLOAD,
  },
  S145: {
    type: SubjectivityTypes.YES_NO,
  },
  S147: {
    type: SubjectivityTypes.UPLOAD,
  },
  S149: {
    type: SubjectivityTypes.ATTEST,
    prompt:
      "Please confirm the insured will set up a call with Cowbell's Resiliency Services team prior to bind.",
  },
  S150: {
    type: SubjectivityTypes.YES_NO,
    hasUpload: true,
    alwaysShowUpload: true,
    uploadCondition: () => true,
    prompt:
      'Please confirm employee cyber awareness training or a plan to implement is in place, and upload supporting documentation.',
  },
  S156: {
    type: SubjectivityTypes.ATTEST,
    prompt:
      'Please confirm the Warranty Statement (or Increased Limits Warranty Statement, if applicable) is signed and currently dated by the President, CEO, CFO, or an equivalent executive officer.',
  },
  S157: {
    type: SubjectivityTypes.UPLOAD,
  },
} as const;

export const surplusLinesStates = {
  LA: 'LA',
  MT: 'MT',
} as const;

export const orderedPreBindSteps = [
  bindSteps.POLICY_HOLDER_INFORMATION,
  bindSteps.AGENCY_INFORMATION,
  bindSteps.POLICY_EFFECTIVE_DATE,
  bindSteps.DILLIGENT_SEARCH_REPORT,
  bindSteps.MANUFACTURING,
  bindSteps.TECH_E_O_COV_CONFIRMATION,
  bindSteps.NO_KNOWN_LOSS_LETTER,
  bindSteps.LOSS_RUNS,
  bindSteps.INSURANCE_APPLICATION,
] as const;

export const orderedPreBindSubjectivities = [
  bindSteps.POLICY_HOLDER_INFORMATION,
  bindSteps.AGENCY_INFORMATION,
  bindSteps.DILLIGENT_SEARCH_REPORT,
  bindSteps.MANUFACTURING,
  bindSteps.TECH_E_O_COV_CONFIRMATION,
  bindSteps.NO_KNOWN_LOSS_LETTER,
  bindSteps.LOSS_RUNS,
  bindSteps.INSURANCE_APPLICATION,
] as const;

export const orderedPreIssueSteps = [
  ...orderedPreBindSteps,
  bindSteps.BIND_QUOTE,
  bindSteps.ADDITIONAL_NAMED_INSURED,
] as const;

export const orderedBindSteps = [
  ...orderedPreBindSteps,
  ...orderedPreIssueSteps,
  bindSteps.ISSUE_POLICY,
] as const;

export const PRE_ISSUANCE = 'PreIssuance';
export const POST_ISSUANCE = 'PostIssuance';

export const manufactruingCheckboxLabel = `I confirm that the manufacturing environment is separated and inaccessible
from the internet-facing network. This control is required prior to
binding.`;

export const financialsCheckboxLabel = 'Attest';

export const attestationQuoteCheckBoxLabel = (
  str: string
) => `I understand that by ${str} this box, I am representing and
warranting that the document(s) uploaded and information provided.`;

export const techENOCheckboxLabel =
  'I confirm that the named insured has their own Tech E&O policy in force.';

export const tooltipLegalLanguage =
  'In doing so, I attest that the information presented is a complete, true and accurate representation on this business and understand that any fraudulent intent is subject to criminal and civil penalties.';

export const ANIAffidavitCheckboxLabel = `I confirm the Cowbell Additional Named Insured Affidavit is clearly signed
and dated by the named insured contact within the last 30 days.`;

export const insuranceApplicationCheckboxLabel = `${t(
  'check'
)} to verify signature and date of application`;

export const securityQuestionsCheckboxLabel = `${t(
  'check'
)} to verify security questions above are answered accurately.`;

export const noKnowLossLetterCheckboxLabel = `${t(
  'check'
)} to verify signature and date of NKLL.`;

export const lossRunsCheckboxLabel = `${t(
  'check'
)} to verify date and accuracy of Loss Runs.`;

export const dilligentSearchReportCheckBoxLabel = `${t(
  'check'
)} to verify the Diligent Search Report is clearly signed and dated by the Named Insured contact.`;

export const MTComplianceCheckBox = `MONTANA SURPLUS LINES NOTICE`;

export const LAComplianceCheckBox = `LOUISIANA SURPLUS LINES NOTICE`;

export const acceptedUploadDocs =
  'image/png, image/jpg, image/jpeg, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const acceptedUploadDocsPreviewText = `image/png, image/jpg, image/jpeg, application/pdf, application/doc`;

// P100 constants

export const insuranceAppNkll = `NKLL is attached with the signed application`;

export const commissionPercentageLabel = `${t(
  'check'
)} to verify commission percentage`;
